import * as def from '@/definitions'
import { Layer } from './layer'
import { SVGPathData, SVGPathDataTransformer, SVGPathDataParser } from 'svg-pathdata';
import { Vector2 } from '@/utilityModels/vector2';

export class PathLayer extends Layer
{
    public get layerType() : string { return "path"; }

    // パス(SVG形式)
    private _svgPath : string = "";
    public get svgPath() : string { return this._svgPath; }
    public set svgPath(svgPath : string) { this._svgPath = svgPath; }

    // パスの回転角度
    private _pathRotate : number = 0;

    public get x() : number
    {
        // 回転後の座標を得る
        return this.getRotatedPosition().x;
    }

    public set x(x : number)
    {
        if(this._svgPath == "") { return; }
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 回転後の座標を得る
        var rotatedX = this.getRotatedPosition().x;
        // セットしたいx座標との差を出す
        var deltaX = x - rotatedX;
        // 移動してパスを返す
        this._svgPath = pathData.translate(deltaX, 0).encode();
    }

    public get y() : number
    {
        // 回転後の座標を得る
        return this.getRotatedPosition().y;
    }

    public set y(y : number)
    {
        if(this._svgPath == "") { return; }
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 回転後の座標を得る
        var rotatedY = this.getRotatedPosition().y;
        // セットしたいｙ座標との差を出す
        var deltaY = y - rotatedY;
        // 移動してパスを返す
        this._svgPath = pathData.translate(0, deltaY).encode();
    }

    // 回転したポジションを得る。
    private getRotatedPosition() : Vector2
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // x,y座標を得る
        var x = bounds.minX;
        var y = bounds.minY;
        var vector = new Vector2(x, y);
        // 中心で回転する
        return vector.rotateDegrees(this.rotate, (bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2);
    }

    public get width() : number
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // 幅を返す
        return bounds.maxX - bounds.minX;
    }

    public set width(width : number)
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        if (bounds.maxX - bounds.minX > 0)
        {
            // 幅に対する拡大率を出す
            var scale = width / (bounds.maxX - bounds.minX);
            // 移動してパスを返す
            this._svgPath = pathData.scale(scale, 1).encode();
        }
    }

    public get height() : number
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // 幅を返す
        return bounds.maxY - bounds.minY;
    }

    public set height(height : number)
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        if (bounds.maxY - bounds.minY > 0)
        {
            // 高さに対する拡大率を出す
            var scale = height / (bounds.maxY - bounds.minY);
            // 移動してパスを返す
            this._svgPath = pathData.scale(1, scale).encode();
        }
    }

    public get rotate() : number
    {
        return this._pathRotate;
    }

    public set rotate(rotate : number)
    {
        this._pathRotate = rotate;
    }

    public get originX() : number
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // 左端を返す
        return bounds.minX;
    }

    public get originY() : number
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // 上端を返す
        return bounds.minY;
    }

    public isPointContained(x : number, y : number) : boolean
    {
        // パスデータを取得
        var pathData = new SVGPathData(this._svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // 与えられた点を回転角だけ反時計回転
        var cX = (bounds.minX + bounds.maxX) / 2;
        var cY = (bounds.minY + bounds.maxY) / 2;
        var rad = this.rotate * Math.PI / 180;
        var rX = (x - cX) * Math.cos(-rad) - (y - cY) * Math.sin(-rad) + cX;
        var rY = (x - cX) * Math.sin(-rad) + (y - cY) * Math.cos(-rad) + cY;
        // とりあえず矩形でチェックする
        return (bounds.minX <= rX && rX <= bounds.maxX && 
            bounds.minY <= rY && rY <= bounds.maxY);
    }

    // 複製
    public clone()
    {
        var cloned = new PathLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : PathLayer)
    {
        layer.svgPath = this._svgPath;
        layer._pathRotate = this._pathRotate;
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.svgPath = this._svgPath;
        jsonObject.pathRotate = this._pathRotate;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : Layer
    {
        var layer = new PathLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.svgPath != null) { this._svgPath = jsonData.svgPath; }
        if(jsonData.pathRotate != null) { this._pathRotate = jsonData.pathRotate; }

        super.loadParameterFromJson(jsonData);
    }
}